import { Injectable } from '@angular/core';

const QWERTY_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

function hasQwertySequence(password: string, minLength = 3): boolean {
  const pwd = password.toLowerCase();
  for (const row of QWERTY_ROWS) {
    for (let i = 0; i <= row.length - minLength; i++) {
      const seq = row.substring(i, i + minLength);
      if (pwd.includes(seq)) {
        return true;
      }
    }
    const reversed = row.split('').reverse().join('');
    for (let i = 0; i <= reversed.length - minLength; i++) {
      const seq = reversed.substring(i, i + minLength);
      if (pwd.includes(seq)) {
        return true;
      }
    }
  }
  return false;
}

const numberSequenceRegex = /(012|123|234|345|456|567|678|789|890|098|987|876|765|654|543|432|321|210)/;
const letterSequenceRegex =
  /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|zyx|yxw|xwv|wvu|vut|uts|tsr|srq|rqp|qpo|pon|onm|nml|mlk|lkj|kji|jih|ihg|hgf|gfe|fed|edc|dcb|cba)/i;

export interface PasswordEvaluation {
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  noSequentialChars: boolean;
  noSequentialLetters: boolean;
  noSequentialDigits: boolean;
  noWhitespace: boolean;
  hasValidLength: boolean;
}

export function evaluatePassword(password: string): PasswordEvaluation {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[\W_]/.test(password);
  const noWhitespace = !/\s/.test(password);
  const hasValidLength = password.length >= 8 && password.length <= 16;

  const hasSequentialLetters = letterSequenceRegex.test(password) || hasQwertySequence(password, 3);
  const hasSequentialDigits = numberSequenceRegex.test(password);

  return {
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSpecialChar,
    noSequentialChars: !hasSequentialLetters && !hasSequentialDigits,
    noSequentialLetters: !hasSequentialLetters,
    noSequentialDigits: !hasSequentialDigits,
    noWhitespace,
    hasValidLength
  };
}

export function isStrongPassword(password: string): boolean {
  const e = evaluatePassword(password || '');
  return !!(e.hasUpperCase && e.hasLowerCase && e.hasNumber && e.hasSpecialChar && e.hasValidLength && e.noSequentialChars && e.noWhitespace);
}

@Injectable({ providedIn: 'root' })
export class PasswordService {
  evaluatePassword(password: string): PasswordEvaluation {
    return evaluatePassword(password);
  }

  isStrongPassword(password: string): boolean {
    return isStrongPassword(password);
  }
}
